import { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Autocomplete,
  ChipList,
  FormControls,
  InfoTooltip,
  Switch,
  TextField,
} from '@elementary/common-ui/components';
import { ValueLabelOption } from '@elementary/common-ui/types';
import {
  EMPTY_YAML_FORM_RESOURCE_OPTIONS,
  YamlFormField as YamlFormFieldDescriptor,
  YamlFormResourceOptions,
  YamlFormValues,
} from './types';
import { getValueAtPath, setValueAtPath } from './yamlFormValues';

const compactControlSx = { width: '220px' };

const NestedFields = styled(FormControls.NestedFields)`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: center;
  column-gap: 16px;
`;

type Props = {
  field: YamlFormFieldDescriptor;
  values: YamlFormValues;
  onChange: (values: YamlFormValues) => void;
  resourceOptions?: YamlFormResourceOptions;
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string');
};

const toTextValue = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};

const isDecimalDraft = (text: string) => /^-?\d*\.?\d*$/.test(text);

const isCompleteDecimal = (text: string) =>
  text !== '' &&
  text !== '-' &&
  text !== '.' &&
  text !== '-.' &&
  !text.endsWith('.') &&
  Number.isFinite(Number(text));

const hasStringValue = (value: unknown): value is { value: string } =>
  typeof value === 'object' &&
  value !== null &&
  'value' in value &&
  typeof value.value === 'string';

const toStoredString = (value: unknown): string | undefined => {
  if (value == null || value === '') {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (hasStringValue(value)) {
    return value.value;
  }
  return undefined;
};

const toStoredStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(toStoredString)
    .filter((item): item is string => item !== undefined);
};

const getFieldOptions = (
  field: YamlFormFieldDescriptor,
  resourceOptions: YamlFormResourceOptions,
): Array<string | ValueLabelOption> => {
  if (field.enumValues) {
    return field.enumValues;
  }
  if (field.optionSource) {
    return resourceOptions[field.optionSource];
  }
  return [];
};

const parseFallbackText = (text: string): unknown => {
  if (text === '') {
    return undefined;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const FieldTooltip = ({ description }: { description?: string }) =>
  description ? <InfoTooltip title={description} /> : null;

const FormField = ({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}) => (
  <>
    <FormControls.FormText sx={{ width: '100%', whiteSpace: 'nowrap' }}>
      {`${label}:`}
    </FormControls.FormText>
    <FormControls.Row sx={{ minWidth: 0 }}>
      {children}
      <FieldTooltip description={description} />
    </FormControls.Row>
  </>
);

const JsonFormField = ({
  field,
  value,
  onChange,
}: {
  field: YamlFormFieldDescriptor;
  value: unknown;
  onChange: (next: unknown) => void;
}) => {
  const serialized = toTextValue(value);
  const [text, setText] = useState(serialized);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setText(serialized);
    }
  }, [isFocused, serialized]);

  useEffect(() => {
    if (typeof value !== 'string' || value === '') {
      return;
    }
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object' && parsed !== null) {
        onChange(parsed);
      }
    } catch {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <FormField label={field.label} description={field.description}>
      <TextField
        sx={{ width: '100%' }}
        size="large"
        value={text}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          if (next === '') {
            onChange(undefined);
            return;
          }
          try {
            onChange(JSON.parse(next));
          } catch {
            return;
          }
        }}
      />
    </FormField>
  );
};

const DecimalFormField = ({
  field,
  value,
  onChange,
}: {
  field: YamlFormFieldDescriptor;
  value: unknown;
  onChange: (next: unknown) => void;
}) => {
  const serialized = value === undefined || value === null ? '' : String(value);
  const [text, setText] = useState(serialized);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setText(serialized);
    }
  }, [isFocused, serialized]);

  return (
    <FormField label={field.label} description={field.description}>
      <TextField
        sx={compactControlSx}
        size="large"
        value={text}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          if (text !== '' && !isCompleteDecimal(text)) {
            setText(serialized);
          }
        }}
        onChange={(event) => {
          const next = event.target.value;
          if (!isDecimalDraft(next)) {
            return;
          }
          setText(next);
          if (next === '') {
            onChange(undefined);
            return;
          }
          if (isCompleteDecimal(next)) {
            onChange(Number(next));
          }
        }}
      />
    </FormField>
  );
};

const YamlFormField = ({
  field,
  values,
  onChange,
  resourceOptions = EMPTY_YAML_FORM_RESOURCE_OPTIONS,
}: Props) => {
  const value = getValueAtPath(values, field.path);
  const handleChange = (next: unknown) => {
    onChange(setValueAtPath(values, field.path, next));
  };
  const options = getFieldOptions(field, resourceOptions);
  const isFreeSolo = Boolean(field.optionSource);

  if (field.fieldType === 'object') {
    return (
      <>
        <FormControls.FormTitle sx={{ gridColumn: '1 / -1' }}>
          {field.label}
        </FormControls.FormTitle>
        <NestedFields>
          {field.children?.map((child) => (
            <YamlFormField
              key={child.path.join('.')}
              field={child}
              values={values}
              onChange={onChange}
              resourceOptions={resourceOptions}
            />
          ))}
        </NestedFields>
      </>
    );
  }

  if (field.fieldType === 'autocomplete') {
    return (
      <FormField label={field.label} description={field.description}>
        <Autocomplete
          sx={
            field.optionSource === 'tables'
              ? { width: '100%' }
              : compactControlSx
          }
          size="large"
          options={options}
          freeSolo={isFreeSolo}
          autoSelect={isFreeSolo}
          value={typeof value === 'string' ? value : null}
          onChange={(_, next) => handleChange(toStoredString(next))}
        />
      </FormField>
    );
  }

  if (field.fieldType === 'multiAutocomplete') {
    return (
      <FormField label={field.label} description={field.description}>
        <Autocomplete
          sx={{ width: '100%' }}
          size="large"
          multiple
          filterSelectedOptions
          options={options}
          freeSolo={isFreeSolo}
          autoSelect={isFreeSolo}
          value={toStringArray(value)}
          onChange={(_, next) => handleChange(toStoredStringArray(next))}
        />
      </FormField>
    );
  }

  if (field.fieldType === 'stringList') {
    return (
      <FormField label={field.label} description={field.description}>
        <ChipList
          value={toStringArray(value)}
          onChange={handleChange}
          buttonText="Add value"
        />
      </FormField>
    );
  }

  if (field.fieldType === 'boolean') {
    return (
      <FormField label={field.label} description={field.description}>
        <Switch
          checked={value === true}
          onChange={(_, checked) => handleChange(checked)}
        />
      </FormField>
    );
  }

  if (field.fieldType === 'json') {
    return (
      <JsonFormField field={field} value={value} onChange={handleChange} />
    );
  }

  if (field.fieldType === 'decimal') {
    return (
      <DecimalFormField field={field} value={value} onChange={handleChange} />
    );
  }

  if (field.fieldType === 'numeric') {
    return (
      <FormField label={field.label} description={field.description}>
        <TextField
          sx={compactControlSx}
          numeric
          size="large"
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(event) => {
            const next = event.target.value;
            if (next === '') {
              handleChange(undefined);
              return;
            }
            const parsed = Number(next);
            if (Number.isFinite(parsed)) {
              handleChange(parsed);
            }
          }}
        />
      </FormField>
    );
  }

  const handleTextChange = (text: string) => {
    if (typeof value === 'object' && value !== null) {
      handleChange(parseFallbackText(text));
      return;
    }
    handleChange(text === '' ? undefined : text);
  };

  return (
    <FormField label={field.label} description={field.description}>
      <TextField
        sx={{ width: '100%' }}
        size="large"
        value={toTextValue(value)}
        onChange={(event) => handleTextChange(event.target.value)}
      />
    </FormField>
  );
};

export default YamlFormField;
